import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    TextField,
    Button,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    OutlinedInput,
    FormControlLabel,
    Checkbox,
    Alert,
    Card,
    CardContent,
    Divider,
    Stack,
    IconButton,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    Schedule as ScheduleIcon,
    FilterList as FilterIcon,
    Preview as PreviewIcon,
    PlayArrow as PlayArrowIcon,
    Stop as StopIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

export default function CreateBroadcast() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [selectedBots, setSelectedBots] = useState([]);
    const [bots, setBots] = useState([]);
    const [priority, setPriority] = useState(0);
    const [scheduledAt, setScheduledAt] = useState(null);
    const [useSchedule, setUseSchedule] = useState(false);

    const [filters, setFilters] = useState({
        userStatus: 'all',
        minBalance: 0,
        maxBalance: null,
        hasOrders: null,
        lastActivityDays: null
    });

    const [recurring, setRecurring] = useState({
        enabled: false,
        interval: 'days',
        frequency: 1,
        endAfter: null
    });

    const [loading, setLoading] = useState(false);
    const [estimatedUsers, setEstimatedUsers] = useState(null);

    useEffect(() => {
        loadBots();
    }, []);

    const loadBots = async () => {
        try {
            const response = await axios.get('/telegram-bots');
            setBots(response.data);
        } catch (error) {
            enqueueSnackbar('Ошибка загрузки ботов', { variant: 'error' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title.trim()) {
            enqueueSnackbar('Введите название рассылки', { variant: 'warning' });
            return;
        }

        if (!message.trim()) {
            enqueueSnackbar('Введите текст сообщения', { variant: 'warning' });
            return;
        }

        if (selectedBots.length === 0) {
            enqueueSnackbar('Выберите хотя бы одного бота', { variant: 'warning' });
            return;
        }

        try {
            setLoading(true);

            const data = {
                title,
                message,
                bots: selectedBots,
                filters,
                priority,
                scheduledAt: useSchedule ? scheduledAt : null,
                recurring
            };

            await axios.post('/broadcasts', data);

            enqueueSnackbar('Рассылка создана успешно', { variant: 'success' });
            navigate('/broadcasts');
        } catch (error) {
            console.error('Error creating broadcast:', error);
            enqueueSnackbar(error.response?.data?.message || 'Ошибка создания рассылки', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigate('/broadcasts')}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" component="h1">
                    Создать рассылку
                </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Основная информация
                                </Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Stack spacing={3}>
                                    <TextField
                                        label="Название рассылки"
                                        fullWidth
                                        required
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="Например: Акция на товары"
                                        helperText="Краткое описание рассылки для идентификации в списке"
                                    />

                                    <TextField
                                        label="Текст сообщения"
                                        fullWidth
                                        required
                                        multiline
                                        rows={8}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Введите текст сообщения для рассылки..."
                                        helperText={`Символов: ${message.length} / 4096. Поддерживаются эмодзи и форматирование.`}
                                    />

                                    <Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                            <Box>
                                                <Typography variant="body2" color="text.secondary">
                                                    Выберите ботов *
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    Сообщение будет отправлено через выбранных ботов
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1}>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        const personalBots = bots.filter(b => b.botType === 'personal');
                                                        setSelectedBots(personalBots.map(b => b._id));
                                                    }}
                                                >
                                                    Личные боты
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => {
                                                        const publicBots = bots.filter(b => b.botType === 'public');
                                                        setSelectedBots(publicBots.map(b => b._id));
                                                    }}
                                                >
                                                    Публичные боты
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    onClick={() => setSelectedBots(bots.filter(b => b.botType === 'private').map(b => b._id))}
                                                >
                                                    Приватные боты
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        if (selectedBots.length === bots.length) {
                                                            setSelectedBots([]);
                                                        } else {
                                                            setSelectedBots(bots.map(b => b._id));
                                                        }
                                                    }}
                                                >
                                                    {selectedBots.length === bots.length ? 'Снять все' : 'Выбрать всех'}
                                                </Button>
                                            </Stack>
                                        </Box>
                                        <FormControl fullWidth required>
                                            <Select
                                                multiple
                                                value={selectedBots}
                                                onChange={(e) => setSelectedBots(e.target.value)}
                                                displayEmpty
                                                renderValue={(selected) => {
                                                    if (selected.length === 0) {
                                                        return <em>Не выбрано</em>;
                                                    }
                                                    return (
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                            {selected.map((value) => {
                                                                const bot = bots.find(b => b._id === value);
                                                                return (
                                                                    <Chip
                                                                        key={value}
                                                                        label={bot?.username || value}
                                                                        size="small"
                                                                    />
                                                                );
                                                            })}
                                                        </Box>
                                                    );
                                                }}
                                            >
                                                {bots.map((bot) => (
                                                    <MenuItem key={bot._id} value={bot._id}>
                                                        @{bot.username} ({bot.botType === 'private' ? 'Приватный' : bot.botType === 'personal' ? 'Личный' : 'Публичный'})
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Box>
                                </Stack>
                            </Paper>

                            <Paper sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <FilterIcon color="primary" />
                                    <Typography variant="h6">
                                        Фильтры получателей
                                    </Typography>
                                </Box>
                                <Divider sx={{ mb: 3 }} />

                                <Alert severity="info" sx={{ mb: 2 }}>
                                    Фильтры помогают отправить сообщение только нужной аудитории. Все условия работают вместе (И).
                                </Alert>

                                <Stack spacing={3}>
                                    <FormControl fullWidth>
                                        <InputLabel>Статус пользователя</InputLabel>
                                        <Select
                                            value={filters.userStatus}
                                            onChange={(e) => handleFilterChange('userStatus', e.target.value)}
                                            label="Статус пользователя"
                                        >
                                            <MenuItem value="all">Все пользователи</MenuItem>
                                            <MenuItem value="active">Только активные</MenuItem>
                                            <MenuItem value="blocked">Только заблокированные</MenuItem>
                                        </Select>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                                            Активные - не заблокированные в системе. Рекомендуется для рассылок.
                                        </Typography>
                                    </FormControl>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            Диапазон баланса (₽)
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Минимальный баланс"
                                                    type="number"
                                                    fullWidth
                                                    value={filters.minBalance}
                                                    onChange={(e) => handleFilterChange('minBalance', Number(e.target.value))}
                                                    helperText="От какой суммы"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    label="Максимальный баланс"
                                                    type="number"
                                                    fullWidth
                                                    value={filters.maxBalance || ''}
                                                    onChange={(e) => handleFilterChange('maxBalance', e.target.value ? Number(e.target.value) : null)}
                                                    helperText="До какой суммы"
                                                />
                                            </Grid>
                                        </Grid>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5, display: 'block' }}>
                                            Например: от 1000₽ до 10000₽ - отправит только пользователям с балансом в этом диапазоне
                                        </Typography>
                                    </Box>

                                    <FormControl fullWidth>
                                        <InputLabel>Наличие заказов</InputLabel>
                                        <Select
                                            value={filters.hasOrders === null ? 'all' : filters.hasOrders ? 'yes' : 'no'}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                handleFilterChange('hasOrders', val === 'all' ? null : val === 'yes');
                                            }}
                                            label="Наличие заказов"
                                        >
                                            <MenuItem value="all">Не важно</MenuItem>
                                            <MenuItem value="yes">Есть заказы</MenuItem>
                                            <MenuItem value="no">Нет заказов</MenuItem>
                                        </Select>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                                            Фильтр по истории заказов. "Есть заказы" - только покупатели, "Нет заказов" - новички.
                                        </Typography>
                                    </FormControl>

                                    <TextField
                                        label="Активность за последние дни"
                                        type="number"
                                        fullWidth
                                        value={filters.lastActivityDays || ''}
                                        onChange={(e) => handleFilterChange('lastActivityDays', e.target.value ? Number(e.target.value) : null)}
                                        helperText="Например: 7 - отправит только тем, кто был активен за последнюю неделю. Пусто = всем."
                                    />
                                </Stack>
                            </Paper>
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Stack spacing={3}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        Настройки отправки
                                    </Typography>
                                    <Divider sx={{ my: 2 }} />

                                    <Stack spacing={2}>
                                        <TextField
                                            label="Приоритет"
                                            type="number"
                                            fullWidth
                                            value={priority}
                                            onChange={(e) => setPriority(Number(e.target.value))}
                                            helperText="Чем выше число, тем выше приоритет"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={useSchedule}
                                                    onChange={(e) => setUseSchedule(e.target.checked)}
                                                />
                                            }
                                            label="Запланировать отправку"
                                        />

                                        {useSchedule && (
                                            <DateTimePicker
                                                label="Дата и время отправки"
                                                value={scheduledAt}
                                                onChange={setScheduledAt}
                                                renderInput={(params) => <TextField {...params} fullWidth />}
                                                minDateTime={new Date()}
                                            />
                                        )}

                                        <Divider />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={recurring.enabled}
                                                    onChange={(e) => setRecurring(prev => ({ ...prev, enabled: e.target.checked }))}
                                                />
                                            }
                                            label="Периодическая рассылка"
                                        />

                                        {recurring.enabled && (
                                            <Stack spacing={2}>
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <TextField
                                                            label="Повторять каждые"
                                                            type="number"
                                                            fullWidth
                                                            value={recurring.frequency}
                                                            onChange={(e) => setRecurring(prev => ({ ...prev, frequency: Number(e.target.value) }))}
                                                            inputProps={{ min: 1 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <FormControl fullWidth>
                                                            <InputLabel>Интервал</InputLabel>
                                                            <Select
                                                                value={recurring.interval}
                                                                onChange={(e) => setRecurring(prev => ({ ...prev, interval: e.target.value }))}
                                                                label="Интервал"
                                                            >
                                                                <MenuItem value="minutes">Минут</MenuItem>
                                                                <MenuItem value="hours">Часов</MenuItem>
                                                                <MenuItem value="days">Дней</MenuItem>
                                                                <MenuItem value="weeks">Недель</MenuItem>
                                                                <MenuItem value="months">Месяцев</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Grid>
                                                </Grid>

                                                <TextField
                                                    label="Остановить после (количество повторений)"
                                                    type="number"
                                                    fullWidth
                                                    value={recurring.endAfter || ''}
                                                    onChange={(e) => setRecurring(prev => ({ ...prev, endAfter: e.target.value ? Number(e.target.value) : null }))}
                                                    helperText="Оставьте пустым для бесконечного повторения"
                                                    inputProps={{ min: 1 }}
                                                />

                                                <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                                                    Рассылка будет автоматически повторяться каждые {recurring.frequency}{' '}
                                                    {recurring.interval === 'minutes' && 'минут'}
                                                    {recurring.interval === 'hours' && 'часов'}
                                                    {recurring.interval === 'days' && 'дней'}
                                                    {recurring.interval === 'weeks' && 'недель'}
                                                    {recurring.interval === 'months' && 'месяцев'}
                                                    {recurring.endAfter && ` (максимум ${recurring.endAfter} раз)`}
                                                </Alert>
                                            </Stack>
                                        )}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <Alert severity="info">
                                <Typography variant="body2">
                                    Рассылка будет добавлена в очередь и отправлена автоматически.
                                    Если есть активная рассылка, новая будет ожидать своей очереди.
                                </Typography>
                            </Alert>

                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                fullWidth
                                disabled={loading}
                                startIcon={<SendIcon />}
                            >
                                {loading ? 'Создание...' : 'Создать рассылку'}
                            </Button>

                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={() => navigate('/broadcasts')}
                                disabled={loading}
                            >
                                Отмена
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
}
